import { useEffect, useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { einheitFuer, useT } from "../i18n";
import { Knopf, Marke, Punkt, euro } from "./index";
import { Postenaufstellung, Posteneinstellungen, Postenteil } from "./Postenaufstellung";
import { mengeFolgtAusAufmass, mengentext, mitMengenfeld } from "./Leistungsmenge";
import { Abholung, AbholungInZeile } from "./Abholung";
import { abholungAusPreis, abholungMoeglich } from "../daten/abholung";
import { Gespann } from "./Gespann";
import { ZeileDazu } from "./Merkzeile";
import { ARTWORT } from "./Positionen";
import type { Spanne } from "./Zeitraum";
import { gemeinsamerMietzeitraum, zeitraumEinzeln } from "../daten/eigenerzeitraum";
import { Zeichen } from "./Zeichen";
import { Feiertagshinweis } from "./Feiertage";
import { Postenblatt } from "./Postenblatt";
import { MerklisteAbschluss, type Abschlussart } from "./MerklisteAbschluss";
import { MerklisteFab } from "./MerklisteFab";
import { lageWort, punktVon, wiederFreiSatz } from "./Trefferzeile";
import { tagMitWochentag } from "./preistext";
import { useTelefon } from "../schale/geraet";
import { useDarfSchreiben, useSitzung } from "../schale/Sitzung";
import { useMaschinentypen, usePreis } from "../daten/haken";
import { tagKurz, useNachschlagen, type Typenlage } from "../daten/nachschlagen";
import {
  alsMerkzuschlag, damalsWert, istMiete, postenAendern, postenSumme, schonGezaehltJePosten, useMerkliste, zuschlaegeZusammenfuehren, type Merkposten, fehlendePreise,
} from "../daten/merkliste";
import "../stile/merkliste.css";
import "../stile/vermieten.css";
/* Die Telefonregeln des Vermietens — direkt danach, damit die Kaskade klar ist. */
import "../stile/telefon/vermieten.css";
import { vormerkungWer } from "../daten/reserviertsatz";
import { usePreisfrageMelden, usePreiseUnterwegs } from "../daten/preisfrage";

/* ══════════════════════════════════════════════════════════════════
   Die Leiste unten rechts — der Warenkorb der Preisauskunft.

   Luca, 17.09.2026: *„Dann ist es wie ein Warenkorb, und dann kann ich
   weitermachen und noch Preise für was anderes suchen. **Unten rechts in
   der Ecke habe ich generell zwei Buttons: jetzt vermieten und Angebot
   erstellen.**"*

   „Generell" ist das Wort, an dem diese Datei hängt: Die Leiste gehört
   nicht zur Preisauskunft, sie gehört zur Anwendung. Sie steht deshalb in
   der Schale (`schale/Schale.tsx`) und nicht in einer Maske — wer nach
   der dritten Auskunft ins Brett wechselt, um einen Zeitraum zu prüfen,
   soll seine drei Posten dort wiederfinden. Und sie nimmt aus **beiden**
   Türen an: aus der Preisauskunft mit Betrag, aus dem Verfügbarkeitscheck
   ohne (`daten/merkliste.ts`).

   **Sie zeigt sich nur, wenn etwas darinsteht.** Eine leere Leiste wäre
   ein Knopf, der nichts tut, in einer Ecke, die sonst frei ist. Deshalb
   ist sie in zwei Teile geschnitten: `Merkliste` liest die Liste und
   hört auf, wenn sie leer ist; erst `Leiste` darunter fragt Stammdaten
   nach. So kostet eine leere Merkliste keine einzige Abfrage.

   **Sie liegt über dem Deckblatt** (`z-index` in `stile/merkliste.css`).
   Das ist Absicht und nicht Zufall: Gemerkt wird IN der Preisauskunft,
   und die ist ein Deckblatt. Eine Liste, die genau dann verschwindet,
   wenn man sie füllt, beantwortet die Frage „wie viele habe ich schon"
   nicht. Nur der Abschluss selbst schiebt sie weg — dort ist sie das
   Blatt, und zwei Zählwerke übereinander wären eines zu viel.

   Klassen: `ml-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Wo die Sammlung einen **eigenen** Platz im Bild hat, tritt die
 * schwebende Leiste zurück.
 *
 * Luca, 17.09.2026, nachdem er die Fläche gesehen hatte: *„Mach dieses
 * Merkliste und so so, dass das wieder eine Seitenleiste in dem Vermieten
 * ist, weißte? Ist schicker."*
 *
 * **Das ist keine zweite Form für dieselbe Sache, sondern eine
 * Unterscheidung nach Ort.** In der Vermietfläche ist die Sammlung Teil
 * der Arbeit und hat Platz — dort steht sie als Spalte im Bild
 * (`Merklistenspalte`). Überall sonst — Kundenakte, Brett, Angebot — ist
 * sie ein Hinweis, der nicht im Weg sein darf, und bleibt die schwebende
 * Leiste beziehungsweise am Telefon der runde Knopf.
 *
 * Verglichen wird auf den ganzen Pfad: `/vermieten/klassisch` ist die alte
 * Schnellerfassung und hat keine Spalte, also steht dort die Leiste.
 */
const OHNE_LEISTE = ["/vermieten"];

/**
 * Die Merkliste — in der Fassung, die zum Gerät und zum Ort gehört.
 *
 * **Am Telefon ist es ein runder Knopf** (`MerklisteFab.tsx`, Luca am
 * 17.09.2026: *„Auf dem Mobile muss die Merkliste quasi als FAB-Button
 * sein."*), am Schreibtisch die Leiste unten rechts. Nicht zwei Stile
 * für dieselbe Fläche, sondern zwei Formen für zwei Hände: Die Leiste
 * ist auf 390 px so breit wie der Schirm und deckt die Arbeit zu.
 *
 * Entschieden wird es hier und nicht in der Schale, damit die Frage
 * „steht überhaupt etwas darin" vorher beantwortet ist: Eine leere
 * Merkliste kostet weiterhin keine einzige Abfrage.
 */
export function Merkliste() {
  const { posten } = useMerkliste();
  const telefon = useTelefon();
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  /* In der Fläche steht am Schreibtisch die Spalte — am Telefon hat sie
     dort keinen Platz, und die Sammlung ist auch dort der runde Knopf. */
  if (!posten.length || (!telefon && OHNE_LEISTE.includes(pfad))) return null;
  return telefon ? <MerklisteFab /> : <Leiste />;
}

/* ══════════════════════════════════════════════════════════════════
   Der Zustand, den jede Fassung für sich führt.

   Welches Postenblatt offen ist und welcher Abschluss — beides gehört
   der Fassung und nicht der Sammlung: Die Leiste muss zurücktreten,
   solange ein Blatt darüber liegt, die Spalte nicht (sie liegt unter dem
   Deckblatt und nicht darüber).
   ══════════════════════════════════════════════════════════════════ */

function useBlaetter() {
  const [abschluss, setzeAbschluss] = useState<Abschlussart | null>(null);
  const [blatt, setzeBlatt] = useState<string | null>(null);
  return { abschluss, setzeAbschluss, blatt, setzeBlatt };
}

/** Die beiden Deckblätter der Sammlung — in beiden Fassungen dieselben. */
function Blaetter({ blatt, setzeBlatt, abschluss, setzeAbschluss, fertig }: {
  blatt: string | null;
  setzeBlatt: (id: string | null) => void;
  abschluss: Abschlussart | null;
  setzeAbschluss: (a: Abschlussart | null) => void;
  fertig: () => void;
}) {
  const { posten, wer } = useMerkliste();
  const offenerPosten = posten.find((p) => p.id === blatt) ?? null;
  return (
    <>
      {/* Das Blatt am Posten — alles, was die Schnellerfassung an einer
          Zeile kann. Es gehört der Sammlung und nicht einer Maske: Wer aus
          dem Nachschlagen heraus merkt, soll seinen Posten dort genauso
          vertiefen können wie in der Vermietfläche. */}
      <Postenblatt
        offen={offenerPosten !== null}
        posten={offenerPosten}
        kunde={wer.nr}
        schliessen={() => setzeBlatt(null)}
      />

      {/* **Am Abschluss ändert sich nichts** (17.09.2026): „Jetzt
          vermieten" und „Angebot erstellen" öffnen weiter dasselbe
          kompakte Fenster — auch aus der Spalte heraus. */}
      <MerklisteAbschluss
        offen={abschluss !== null}
        art={abschluss ?? "vermieten"}
        schliessen={() => setzeAbschluss(null)}
        fertig={fertig}
      />
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Eine Zeile je Posten — zwei Zeilen hoch, der Rest klappt auf.

   Luca, 18.09.2026, auf 390 × 844: *„Die Merkliste: Du musst diese
   Hoflader und so weiter, diese Kacheln, die wir dort machen, deutlich
   kleiner formatieren und viel, viel besser auf Mobile optimieren."*

   Er hat recht, und die Zahl sagt warum. Eine Karte trug am Telefon
   Namen, Betrag, Zeitraumfeld (44), Lagezeile, Mengenknöpfe (44),
   Satzfeld (44), zwei Schalter und die vollen Angaben — **rund 280 px**.
   Ins Blatt von unten passten damit **anderthalb Posten**. Ein
   Warenkorb, in den man nicht hineinsieht, beantwortet die Frage „was
   habe ich schon" nicht, und genau dafür ist er da.

   ── Die Form ist die der Trefferzeile ─────────────────────────────

   Dieselbe, die das Nachschlagen am Telefon seit dem 18.09.2026 führt
   (`masken/NachschlagenTelefon.tsx`, `.tz`, `bausteine/Trefferzeile.tsx`): **zwei Zeilen,
   Fläche mit Haarlinien, Streifen links in der Farbe des Befunds** —
   keine gerundeten Kacheln mit Luft dazwischen.

   ```
   Minibagger MB-15                          432,50 €
   ● 21.9. – 25.9. · 2 Stück            5 Miettage   +
   ```

   Oben die Sache und was sie kostet, unten wann sie läuft und wofür der
   Betrag gilt. Das ist dieselbe Ordnung wie im Nachschlagen, und das ist
   der Punkt: Wer dort sucht und hier sammelt, liest zweimal dieselbe
   Zeile. **Rund 62 px** statt 280 — es passen jetzt sieben Posten auf
   den Schirm statt anderthalb.

   ── Was aufklappt, statt zu verschwinden ──────────────────────────

   **Nichts von dem, was am 17. und 18.09.2026 dazukam, ist weg.**
   Zeitraum, Menge, Satz, „Kunde holt ab", „Auflieger mitgeben", die
   vollen Angaben und der Weg ins Blatt am Posten stehen unter der
   Zeile, sobald man sie antippt (`.ml-auf`) — dieselben Felder, dieselbe
   Bedienung. Der Unterschied ist, dass sie **einen Tipper entfernt**
   sind statt immer da; bei acht Posten ist das der Unterschied zwischen
   einer Liste und einem Stapel.

   **Was nicht warten darf, steht trotzdem in der Zeile.** Der Streifen
   links trägt den Befund (`randArt`): eine Belegung im neuen Zeitraum,
   zu wenige freie Stücke, oder ein Zuschlag mit einem Betrag, den
   niemand entschieden hat (`platzhalter`). Farbe trägt dabei nie allein
   — das Wort steht daneben (28-designkonzept.md).

   **Die Menge blieb draußen.** Ein Warenkorb im Handel hat den
   Mengenschritt in der Zeile (Hers, Taco Bell); hier ist die Zwei die
   Ausnahme und nicht der Regelfall — jedes Stück wird einzeln
   disponiert (Regel 2), und zwei Knöpfe à 44 px wären eine dritte
   Zeile für einen Fall, der selten eintritt. Steht dort eine Zahl über
   eins, sagt die Zeile es; geändert wird sie aufgeklappt.

   ── Eine Zeile ist eine Zeile — überall ───────────────────────────

   Luca, 18.09.2026: *„Wenn ich die Merkliste öffne, die sich unten links
   öffnet, wenn ich jetzt über Nachschlagen arbeite, kann ich den Zeitraum
   dann nicht mehr anpassen. Ich kann nur den Zeitraum anpassen, wenn ich
   tatsächlich in den Nachschlagewerken bin."*

   Bis dahin gab es die Felder nur in der Spalte der Vermietfläche; in der
   Leiste unten und im Blatt am Telefon standen dieselben Angaben als Text
   („dort gibt es keine Felder, also muss der Text es sagen"). Das war eine
   Unterscheidung nach Ort, und sie war falsch: Wer über das Nachschlagen
   arbeitet, sieht die Sammlung **nur** in der Leiste und käme damit nie an
   den Zeitraum. **Seitdem zeichnet `Sammelkarte` jede Fassung** — Spalte,
   Leiste und Blatt am Telefon (`Sammelliste`); was sich unterscheidet, sind
   die Maße für den Daumen und nicht die Bedienung.

   ── Was aufgeklappt geht und was im Blatt bleibt (Schreibtisch) ────

   **Oft und einteilig aufgeklappt, selten und mehrteilig im Blatt.**
   Seit dem 08.10.2026 steht aufgeklappt dieselbe Ordnung wie im Abschluss
   und im Blatt (`Postenaufstellung.tsx`): erst **Einstellungen** als
   Formular im Raster (Zeitraum, Stück, Bedienung bzw. Menge und Grad),
   dann die **Aufstellung** Bezeichnung · Menge · Einzel · Betrag mit der
   Miete zuerst, den Zuschlägen mit Haken und Einzelpreis und der Summe,
   dann **Transport**. Luca: *„Wenn ich die aufklicke, ist das alles grau
   und hat keine Struktur."* Welches Stück, wer genau fährt, Ausstattung
   und Zubehör sind Listen mit eigenen Abfragen — die brauchen Platz und
   stehen im Blatt am Posten („Details", `Postenblatt.tsx`).

   ── Am Telefon öffnet die Zeile das Blatt (20.09.2026) ─────────────

   Luca: *„Die Position kann ich dann nicht wirklich bearbeiten, nur wenn
   ich jetzt zum Beispiel in der Merkliste den Rüttler klicke und dann auf
   Details klicke. Das finde ich ein bisschen zu unübersichtlich."*

   Bis dahin klappte die Zeile am Telefon auf — Zeitraum, Menge, Satz,
   „Kunde holt ab", die Zuschläge als Griff — und darunter stand der Knopf
   „Details" zum Blatt, in dem dieselben Dinge noch einmal standen. Zwei
   Ebenen für eine Sache, und die erste war die halbe zweite. **Jetzt
   führt ein Tipp auf die Zeile ins Blatt** — derselbe Weg wie im Angebot
   (`Positionen.tsx`, `Positionszeilen` → `Positionsblatt`), und das Blatt
   trägt alles: Zeitraum, Stück und Satz, Zuschläge, Transport, Gerät,
   Bedienung, Anbauteile. Die Zeile selbst bleibt, was sie war: Name,
   Betrag, Zeitraum, Befund — und rechts der Winkel, der sagt, dass es
   weitergeht.

   **Am Schreibtisch bleibt das Aufklappen.** Dort steht die Sammlung als
   Spalte neben der Trefferliste, und ein Zeitraum, den man ohne Deckblatt
   ändert, ist dort der kürzere Weg. Zwei Formen für zwei Hände, eine
   Bedienung: Geändert wird am Posten, gerechnet in `daten/merkliste.ts`.

   ── Warum hier trotzdem gerechnet wird ───────────────────────────

   Ein anderer Zeitraum ist ein anderer Preis. Die Karte fragt deshalb
   `GET /preis` — **aber erst, wenn jemand den Zeitraum angefasst hat**
   (`nachrechnen`). Eine Sammlung, die beim Zeichnen für jede Karte
   fragt, kostet fünf Abfragen für eine Anzeige, die sich nicht geändert
   hat; und der Vergleich „beim Merken X, jetzt Y" gehört in den
   Abschluss, wo er auch danebensteht.

   Menge und Satz rechnet die Karte selbst — dafür braucht niemand die
   andere Seite.

   ── Und warum sie danach auch nachsieht ──────────────────────────

   Luca, 18.09.2026: *„Wenn ich dann den Zeitraum ändere in der Merkliste,
   muss er jetzt sofort sagen: ‚Achtung, Maschine in diesem Zeitraum nicht
   verfügbar', zum Beispiel."*

   Ein anderer Zeitraum ist nicht nur ein anderer Preis, sondern auch eine
   andere Lage. Bis zum 18.09.2026 rechnete die Karte den Betrag neu und
   schwieg über die Belegung — ein Posten, dessen Maschine im neuen Fenster
   vergeben ist, wäre damit ein Angebot, das der Kunde nicht bekommt.

   **Dieselbe Frage wie überall** (`GET /nachschlagen`, `useNachschlagen`):
   Der Abschluss stellt sie je Zeitfenster, das Nachschlagen je Suche, die
   Karte je Posten. Keine zweite Rechenstelle — „ab wann wieder frei"
   entsteht drüben, aus demselben `belegt`.

   **Gefragt wird nach demselben Eingriff wie der Preis**, nicht beim
   Zeichnen (`nachsehen` neben `nachrechnen`): Eine Leiste, die beim
   Aufklappen für jede Karte den Kalender abfragt, kostet fünf Abfragen für
   eine Anzeige, die sich nicht geändert hat.

   **Und sie hält nicht auf.** Der Posten bleibt in der Sammlung, der
   Zeitraum bleibt geändert — es ist eine Warnung und kein Riegel. Ein
   Angebot über etwas, das heute belegt ist, ist erlaubt (Regel 6 und 10);
   verbindlich wird es erst beim „Jetzt vermieten", und dort weist der
   Abschluss es ohnehin ab.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Die Lage eines Postens im gefragten Fenster — als Zeile an seiner Karte.
 *
 * **Die Wörter sind die der Trefferliste** (`lageWort`, `wiederFreiSatz`,
 * `punktVon`) und keine neuen: Wer im Nachschlagen „Belegt · ab Do 24.09.
 * wieder frei" liest und hier etwas anderes, hat zwei Vokabeln für denselben
 * Befund gelernt.
 *
 * **Die Menge zählt mit.** Drei Rüttler zu merken, von denen einer frei ist,
 * ist keine Zusage — dann steht die Zeile in der Achtungsfarbe, mit dem Satz,
 * den auch der Abschluss dafür hat.
 */
function Kartenlage({ p, lage }: { p: Merkposten; lage: Typenlage | undefined }) {
  const t = useT();
  /* Solange die Antwort unterwegs ist, steht das da — und nicht nichts: Eine
     Karte, die nach dem Wechsel kurz schweigt, liest sich wie „alles gut". */
  if (!lage) return <p className="ml-lage still" role="status">{t("allgemein.laedt")}</p>;
  const knapp = lage.bestand > 0 && lage.frei > 0 && lage.frei < p.menge;
  const art = knapp ? "acht" : punktVon(lage.bestand === 0 ? "nein" : lage.lage);
  const wieder = wiederFreiSatz(t, lage);
  return (
    <p className={`ml-lage ${art}`} role="status">
      <Punkt art={art} />
      <b>{lageWort(t, lage)}</b>
      <span>
        {knapp
          ? t("merkliste.freiZuWenig", { frei: lage.frei, menge: p.menge })
          : wieder || t("nachschlagen.freiVon", { frei: lage.frei, bestand: lage.bestand })}
      </span>
    </p>
  );
}

function Sammelkarte({ p, name, oeffneBlatt }: {
  p: Merkposten;
  name: string;
  oeffneBlatt: (id: string) => void;
}) {
  const t = useT();
  const { wer, posten, aendern, aendernWennFrei, entfernen } = useMerkliste();
  /* **Die Anlieferung zählt einmal je Auftrag** (V-20-Nebenbefund,
     03.10.2026): Steht sie schon an einem früheren Posten, trägt diese Karte
     sie nicht noch einmal — sonst ergaben die Karten 1.600,00, die AB 1.585,00.
     Die Aufstellung sagt es an der Zeile des Zuschlags. */
  const nicht = useMemo(() => schonGezaehltJePosten(posten).get(p), [posten, p]);

  /* **Welche Zuschläge in der Sammlung als eigene Zeile stehen.**
     Ein erfasster Zuschlag **ersetzt** den gerechneten desselben Codes — hier
     wie drüben im Abrechnungslauf, wo ein zugesagter Wert gegen einen
     gerechneten gewinnt (`packages/fach/src/abrechnung.ts`). Beide stehen zu
     lassen hieße, denselben Zuschlag zweimal zu summieren: am Schirm 130,00,
     auf der Rechnung 65,00 — genau die stille Abweichung, die Luca an der
     Preisauskunft angestrichen hat.

     Dieselbe Regel stand in der alten Schnellerfassung (`vonHandCodes`); sie
     wird gebraucht, seit eine Wiederholung die Zuschläge von damals als
     eigene Posten anlegt (18.09.2026). */
  const eigeneCodes = useMemo(
    () => new Set(posten.filter((x) => x.art === "zuschlag" && x.zuschlagCode)
      .map((x) => x.zuschlagCode as string)),
    [posten],
  );

  /**
   * Der Zeitraum wurde angefasst — jetzt, und nur jetzt, wird gefragt.
   *
   * **Ein wiederholter Posten fragt ungefragt** (18.09.2026): Er kommt aus
   * einem alten Vorgang und bringt keinen Preis mit — der alte ist
   * eingefroren und gilt für einen anderen Zeitraum (Regel 4). Ohne diese
   * eine Abfrage stünde „Preis offen" an der Karte, und der Vergleich
   * „damals gegen heute" hätte keine zweite Zahl.
   */
  const [nachrechnen, setzeNachrechnen] = useState(
    p.vorlage !== undefined && !p.gerechnet && istMiete(p),
  );
  /**
   * Dasselbe für die Lage — und anders als `nachrechnen` bleibt es stehen:
   * Wer den Zeitraum zweimal ändert, will beide Male wissen, ob es frei ist,
   * und die Antwort steht danach an der Karte, statt wieder zu verschwinden.
   *
   * **Ein Posten, den die Anwendung selbst dazugelegt hat, fragt ungefragt**
   * (`wegen` — der Anhänger bei Selbstabholung, Migration 0127): Nach ihm hat
   * niemand gesucht, also hat auch niemand gesehen, ob er frei ist. Genau
   * das ist der Fall, in dem eine Zusage schiefgeht.
   *
   * **Ein wiederholter Posten ebenso** (18.09.2026): Er kommt aus einem
   * alten Vorgang in ein neues Fenster, und die Frage „ist das Stück von
   * damals überhaupt noch frei" ist die halbe Antwort auf den Anruf.
   */
  const [nachsehen, setzeNachsehen] = useState(
    p.wegen !== undefined || (p.vorlage !== undefined && istMiete(p)),
  );
  /* **Ein Zeitraum, der anderswo geändert wurde, rechnet hier nach**
     (Systemprüfung A-06) — im Abschluss „für alle Posten" etwa. Sonst stand
     an der Karte der neue Zeitraum mit dem Betrag des alten. */
  useEffect(() => {
    if (!p.zeitraumNeu) return;
    setzeNachrechnen(true);
    setzeNachsehen(true);
  }, [p.zeitraumNeu]);
  const telefon = useTelefon();
  /** Aufgeklappt stehen Einstellungen und Aufstellung darunter (Schreibtisch).
   *  Zu ist der Regelfall — nur eine Leistung, deren Menge noch fehlt, steht
   *  offen da: Die Menge ist die eine Angabe, ohne die sie nicht vermietet
   *  werden kann (Luca, 08.10.2026: „direkt die Quadratmeterzahl eintragen"). */
  const [auf, setzeAuf] = useState(() => mitMengenfeld(p) && mengeFolgtAusAufmass(p));
  /* Am Telefon öffnet die Zeile das Blatt; aufgeklappt wird dort nichts. */
  const aufgeklappt = auf && !telefon;
  /* **Aufgeklappt wird gefragt** — die Tafel braucht Einheit und Satz jedes
     Zuschlags. Dieselbe Abfrage wie das Blatt am Posten (gleicher Schlüssel);
     übernommen in den Posten wird die Antwort weiter nur nach einem Eingriff
     (`nachrechnen`). */
  const auskunft = usePreis(
    nachrechnen || aufgeklappt ? p.typId : undefined,
    p.von, p.bis, wer.nr || null, p.bedient, p.varianteId || undefined,
    { halten: true },
  );
  const preis = auskunft.data;
  /* **Solange der Preis unterwegs ist, ist er nicht offen** (N12-01): Nach
     „Wie zuletzt" stand sonst „Preis offen" und darunter „Summe netto 0,00".
     Gemeldet wird nur, wo der Betrag fehlt oder zum alten Zeitraum gehört. */
  const preisKommt = nachrechnen && Boolean(p.typId) && !auskunft.isError
    && (!p.gerechnet || p.zeitraumNeu === true);
  usePreisfrageMelden(p.id, preisKommt);
  /** Die vorgewählten Zuschläge — ohne die, die als eigene Zeile dastehen. */
  const gerechneteZuschlaege = useMemo(
    () => (preis?.zuschlaege ?? []).filter((z) => z.vorgewaehlt && !eigeneCodes.has(z.code)),
    [preis, eigeneCodes],
  );

  useEffect(() => {
    if (!nachrechnen || !preis || auskunft.isPlaceholderData) return;
    setzeNachrechnen(false);
    /* Die Miete, die Miettage, die Einheiten und die Zuschläge kommen
       mit — ein Zeitraum, der sich ändert, ändert sie alle. Bliebe
       `einheiten` stehen, stünde in der Karte weiter „× 5 Tage", während
       die Summe zwölf rechnet. */
    aendern(p.id, {
      gerechnet: true,
      miettage: preis.miettage,
      netto: preis.netto,
      einheiten: preis.menge,
      einheitKey: preis.einheit_key,
      /* **Und was das Gerät an Bedienung verlangt** (Migration 0083). Ein
         Posten aus der Trefferliste bringt es mit; ein wiederholter nicht —
         der alte Vorgang sagt, wer damals gefahren ist, nicht, wer fahren
         muss. Ohne diese Zeile ginge ein Kran aus einer Wiederholung ohne
         Fahrer durch den Abschluss (Regel 8). */
      ...(preis.bedienungspflicht ? { bedienungspflicht: preis.bedienungspflicht } : {}),
      /* **Und ob der Kunde selbst abholen kann** (Systemprüfung A-02): Ein
         wiederholter Posten brachte es nicht mit, und der Schalter „Kunde
         holt ab" fehlte an ihm. */
      ...abholungAusPreis(preis),
      zeitraumNeu: undefined,
      zuschlaegeNetto: gerechneteZuschlaege.reduce((s, z) => s + z.netto, 0),
      /* **Was ein Mensch daran geändert hat, überlebt das Nachrechnen.** Ein
         abgewählter Zuschlag — von Hand oder vom Schalter „Kunde holt ab" —
         kam sonst mit dem nächsten Zeitraum zurück (18.09.2026). Dieselbe
         Zusammenführung wie in `merken`. */
      zuschlaege: zuschlaegeZusammenfuehren(
        p.zuschlaege,
        gerechneteZuschlaege.map(alsMerkzuschlag),
      ),
    });
  }, [nachrechnen, preis, auskunft.isPlaceholderData, aendern, p.id, p.zuschlaege, gerechneteZuschlaege]);

  /* **Die Lage im neuen Fenster** — derselbe Weg wie im Abschluss und im
     Nachschlagen. Ein Typ je Frage: Die Karte weiß, was sie gemerkt hat,
     und fragt nur danach. */
  const lageAbfrage = useNachschlagen(
    { wort: "", gruppe: null, von: p.von, bis: p.bis, typen: [p.typId] },
    nachsehen,
  );
  /* Die letzte Antwort bleibt stehen, solange die neue unterwegs ist
     (`placeholderData`) — sie gehört dann aber zum alten Fenster und darf
     nicht als Auskunft über das neue durchgehen. Dieselbe Prüfung wie im
     Abschluss (`Fenster`). */
  const lage = lageAbfrage.data?.von === p.von && lageAbfrage.data.bis === p.bis
    ? lageAbfrage.data.zeilen.find((z) => z.typId === p.typId)
    : undefined;

  /* ── „Wieder dasselbe Stück" ─────────────────────────────────────
     Der Wunsch aus dem alten Vorgang, und die einzige Antwort, die dazu
     zählt: **ob genau dieses Stück im neuen Fenster zu haben ist.**

     Drei Antworten, nicht zwei (18.09.2026): frei, **durch ein Angebot
     reserviert**, oder belegt. Die Reservierung steht dazwischen — das
     Gerät ist da und nicht gebucht, aber versprochen; wer es nimmt, löst
     die Vormerkung. Sie hier zu verschweigen hieße, jemanden in ein `409`
     (`belegung.reserviert`) zu schicken, das er beim Merken nicht kommen
     sah.

     Gefragt wird nicht extra: Es ist die Antwort, die für die Lage ohnehin
     schon dasteht (`useNachschlagen`, ein Typ, seine Stücke). */
  const wunschstueck = p.vorlage?.maschine ?? "";
  const damalsStueck = wunschstueck
    ? lage?.geraete.find((g) => g.kennung === wunschstueck)
    : undefined;
  /** `null`: noch keine Antwort. Sonst das Wort, das dafür gilt. */
  const stueckLage: "frei" | "reserviert" | "wirdVermietet" | "weg" | null = !wunschstueck || !lage
    ? null
    /* Ausgemustert, verkauft, umbenannt: Was nicht mehr im Bestand steht,
       ist auch nicht frei — derselbe Fall wie in der alten Maske. */
    : !damalsStueck || damalsStueck.werkstatt
      ? "weg"
      : damalsStueck.reserviert
        ? (damalsStueck.reserviert.abschlussLaeuft ? "wirdVermietet" : "reserviert")
        : damalsStueck.lage === "geht" && damalsStueck.tageVergeben === 0
          ? "frei"
          : "weg";
  const stueckGewaehlt = Boolean(wunschstueck) && p.maschine === wunschstueck;

  /** Der Satz von damals — nur, wenn er etwas zu sagen hat. */
  const damals = damalsWert(p);

  const wortName = name || t("merkliste.ohneNamen");
  /* **Nur eine Miete hat ein Gerät** (18.09.2026). Was eine Leistungs-,
     Waren-, Zuschlags- oder Freitextzeile nicht hat — Lage, Streifen, Blatt
     am Posten —, steht an ihrer Zeile einfach nicht. Es ist dieselbe Zeile
     mit weniger darin und keine zweite Zeilenform. */
  const miete = istMiete(p);
  /** Eine Leistung mit Mengeneinheit — sie trägt ein Mengenfeld statt der Stückknöpfe. */
  const mengenfeld = mitMengenfeld(p);

  /* ── Der Streifen links: der Befund, ohne Aufklappen ──────────────
     Zwei Dinge können daran hängen, und beide dürfen nicht warten, bis
     jemand die Zeile antippt: die Lage im geänderten Zeitraum, und ein
     Zuschlag, dessen Betrag niemand entschieden hat (`platzhalter`,
     0032/0036). Was nichts zu melden hat, bekommt keine Farbe — ein
     grüner Balken an acht Zeilen ist ein Muster und keine Auskunft. */
  const lageArt: "gut" | "acht" | "stopp" | null = !miete || !nachsehen || !lage
    ? null
    : lage.bestand > 0 && lage.frei > 0 && lage.frei < p.menge
      ? "acht"
      : punktVon(lage.bestand === 0 ? "nein" : lage.lage);
  const pruefen = p.zuschlaege.some((z) => !z.aus && z.platzhalter === true);
  /* **Preis immer individuell, noch keiner genannt** (0227, E1 — die
     Anlieferung). Rot wie eine Lage ohne Gerät: So geht der Posten nicht als
     Auftrag hinaus. Das Wort steht daneben. */
  const ohnePreis = fehlendePreise(p);
  const randArt: "gut" | "acht" | "stopp" | null = lageArt === "stopp" || ohnePreis.length
    ? "stopp"
    : pruefen || lageArt === "acht" ? "acht" : null;

  /* ── Die zweite Zeile (08.10.2026) ───────────────────────────────
     Luca: *„Die einzelnen Positionen sind farblich komisch."* Zwei ruhige
     Zeilen auf Papier: oben die Sache und der Betrag, darunter leise
     **Art · Menge mit Einheit · Zeitraum** — in derselben Reihenfolge an
     jeder Zeile, ob Miete, Leistung oder Ware. Farbe trägt nur der Befund
     (Punkt und Wort); „gemerkt für …" steht leise am Ende und nicht mehr in
     der Achtungsfarbe — es ist eine Herkunft, keine Warnung. */
  const mengeWort = mengenfeld
    ? (mengeFolgtAusAufmass(p)
      ? t("positionen.mengeAufmass")
      : t("merkliste.mengeEinheit", { zahl: mengentext(p.menge), einheit: einheitFuer(t, p.einheitKey ?? "", p.menge) }))
    : !miete && p.aufmass && p.menge === 0
      ? t("positionen.mengeAufmass")
      : !miete && p.einheitKey
        ? t("merkliste.mengeEinheit", { zahl: mengentext(p.menge), einheit: einheitFuer(t, p.einheitKey, p.menge) })
        : t("merkliste.stueckzahl", { zahl: mengentext(p.menge) });
  const untenTeile = [
    t(ARTWORT[p.art] ?? ""),
    mengeWort,
    p.von && p.bis && (miete || p.art === "leistung")
      ? (p.bis === p.von ? tagKurz(p.von) : t("zeitfrage.vonBis", { von: tagKurz(p.von), bis: tagKurz(p.bis) }))
      : "",
  ].filter(Boolean);
  /* Der Befund — Wort neben dem Punkt, nie Farbe allein. */
  const befund = [
    lageArt && lageArt !== "gut" && lage ? lageWort(t, lage) : "",
    pruefen ? t("merkliste.betragPruefen") : "",
    ohnePreis.length ? t("merkliste.preisFehltKurz", { was: ohnePreis.map((z) => z.bezeichnung).join(", ") }) : "",
  ].filter(Boolean);
  const fremd = p.kunde && p.kunde !== wer.nr ? t("merkliste.nochFuer", { kunde: p.kundeName || p.kunde }) : "";
  const trenn = ` ${t("allgemein.trennpunkt")} `;

  /* **Eine Freitextzeile IST ihr Text** und hat sonst nichts: keinen
     Betrag, keinen Zeitraum, keine Angaben. Sie klappt deshalb auch nicht
     auf — ein Winkel, hinter dem nichts liegt, ist ein leeres Versprechen. */
  if (p.art === "text") {
    return (
      <li className="ml-karte nurtext">
        <input
          className="feld ml-textfeld"
          value={p.bezeichnung}
          aria-label={t("angebotBauen.platzText")}
          placeholder={t("angebotBauen.platzText")}
          onChange={(e) => postenAendern(p.id, { bezeichnung: e.target.value })}
        />
        <button
          type="button"
          className="ml-weg"
          aria-label={t("merkliste.entfernenSchild", { was: wortName })}
          title={t("merkliste.entfernenSchild", { was: wortName })}
          onClick={() => entfernen(p.id)}
        >
          <Zeichen name="kreuz" />
        </button>
      </li>
    );
  }

  const transport = miete && (abholungMoeglich(p) || (Boolean(p.gespann) && !p.wegen));
  /* „Kunde holt ab" hebt „mit Bediener" auf — ein anderer Preis (0096), also nachrechnen. */
  const nachBediener = (weg: boolean) => { if (weg) setzeNachrechnen(true); };
  /* **Der Umschalter direkt in der Zeile** (Lisas Durchgang, 08.10.2026) —
     ohne Aufklappen, nur an Posten mit Transport. */
  const holtAbInZeile = miete && abholungMoeglich(p);

  return (
    <li className={`ml-karte${randArt ? ` ${randArt}` : ""}${aufgeklappt ? " auf" : ""}${holtAbInZeile ? " mit-holtab" : ""}`}>
      {/* **Die ganze Zeile ist der Griff** — wer mit einer Hand arbeitet,
          trifft die Zeile und kein Ziel darin (`.hw-griff`, 09-mobile.md).
          Am Schreibtisch klappt sie auf (der Winkel dreht sich); am Telefon
          öffnet sie das Blatt (siehe oben). */}
      <button
        type="button"
        className="ml-flaeche"
        {...(telefon ? {} : { "aria-expanded": auf })}
        onClick={() => { if (telefon) oeffneBlatt(p.id); else setzeAuf((x) => !x); }}
      >
        <span className="ml-name">{wortName}</span>

        {/* **Kein Betrag ist keine Null.** Wer nur nach einem Gerät gefragt
            hat, hat nicht nach einem Preis gefragt; der Abschluss holt ihn. */}
        <b className={`ml-betrag${p.gerechnet ? "" : " offen"}`}>
          {p.gerechnet
            ? euro(postenSumme(p, nicht))
            : t(preisKommt ? "allgemein.zahlOffen" : "merkliste.preisOffen")}
        </b>

        <span className="ml-unten">
          <span className="ml-unten-text">{untenTeile.join(trenn)}</span>
          {befund.length && randArt ? (
            <span className={`ml-befund ${randArt}`}>
              <Punkt art={randArt} />
              <span>{befund.join(trenn)}</span>
            </span>
          ) : null}
          {fremd ? <span className="ml-fremd">{`${trenn}${fremd}`}</span> : null}
        </span>
        {/* Ein Feiertag im Zeitraum des Postens (0287) — er zählt als Miettag. */}
        {miete ? <Feiertagshinweis von={p.von} bis={p.bis} miettag klasse="ml-feiertag" /> : null}
        <span className={`ml-ecke${auf ? " offen" : ""}`} aria-hidden="true">
          <Zeichen name="weiter" />
        </span>
      </button>

      {holtAbInZeile ? <AbholungInZeile p={p} danach={nachBediener} /> : null}
      <button
        type="button"
        className="ml-weg"
        aria-label={t("merkliste.entfernenSchild", { was: wortName })}
        title={t("merkliste.entfernenSchild", { was: wortName })}
        onClick={() => entfernen(p.id)}
      >
        <Zeichen name="kreuz" />
      </button>

      {aufgeklappt ? (
        <div className="ml-auf">
          {/* **Was der neue Zeitraum für die Maschine heißt.** Erst nach dem
              Eingriff, dafür sofort — und an der Zeile, damit man sieht, bei
              welchem Posten es klemmt. Sie hält nichts auf. */}
          {miete && nachsehen ? <Kartenlage p={p} lage={lage} /> : null}

          {/* ── Einstellungen: ein Formular im Raster ─────────────────────
              Zeitraum, Stück, Bediener bzw. Menge und Grad — Felder 40 px.
              Ein Zeitraum, in dem das Gerät belegt ist, kommt nicht in die
              Sammlung (19.09.2026): Der alte bleibt, und das Fenster sagt
              warum. Ein Stück mehr ist ein Gerät mehr — nur, wenn es frei ist. */}
          <Posteneinstellungen
            p={p}
            zeitraumSetzen={(neu: Spanne) => {
              if (!neu.von || !neu.bis) return;
              if (!miete) { aendern(p.id, { von: neu.von, bis: neu.bis }); return; }
              /* **Einzeln gesetzt ist es sein eigener Zeitraum** (08.10.2026),
                 wenn er von dem der übrigen Mieten abweicht — dann folgt er im
                 Abschluss nicht mehr, wenn dort der gemeinsame geändert wird.
                 Steht er allein, gibt es keinen gemeinsamen, von dem er abwiche. */
              const gemeinsam = gemeinsamerMietzeitraum(posten.filter((x) => x.id !== p.id), { von: null, bis: null });
              const einzeln = gemeinsam.von
                ? zeitraumEinzeln({ von: neu.von, bis: neu.bis }, gemeinsam)
                : { von: neu.von, bis: neu.bis, eigenerZeitraum: undefined };
              void aendernWennFrei(p.id, einzeln).then((geaendert) => {
                if (!geaendert) return;
                setzeNachrechnen(true);
                setzeNachsehen(true);
              });
            }}
            stueckSetzen={(menge) => {
              if (miete && menge > p.menge) void aendernWennFrei(p.id, { menge });
              else aendern(p.id, { menge });
            }}
            bedientSetzen={(bedient) => {
              /* Mit oder ohne Bediener ist ein anderer Preis (Migration
                 0096) — also wird nachgerechnet wie nach einem Zeitraum. */
              aendern(p.id, bedient ? { bedient: true } : { bedient: false, bediener: "" });
              setzeNachrechnen(true);
            }}
            /* **Der Weg ins Blatt am Posten** — für das, was Listen mit eigenen
               Abfragen braucht: welches Stück, wer fährt, Zubehör, Ausstattung.
               Seit dem 19.09.2026 heißt er „Details". */
            kopf={miete ? (
              <button type="button" className="pst-verweis" onClick={() => oeffneBlatt(p.id)}>
                {t("vermieten.blattOeffnen")}
              </button>
            ) : undefined}
          />

          {/* ── Die Aufstellung: Miete, Zuschläge, Summe ──────────────────
              Dieselbe Tafel wie im Abschluss und im Blatt „Details"; jeder
              Zuschlag mit Haken und Einzelpreis. */}
          <Postenaufstellung
            p={p}
            preis={preis}
            nicht={nicht}
            aendern={(teil) => aendern(p.id, teil)}
          />

          {/* **Zwei Schalter, dieselbe Mechanik** (`daten/abholung.ts`): „Kunde
              holt ab" nimmt die Anlieferung weg und hängt den Transportanhänger
              an (0127), „Auflieger mitgeben" hängt den Auflieger an (0072).
              Beide stehen nur, wo sie eine Frage sind. */}
          {transport ? (
            <Postenteil titel={t("merkliste.detailsTransport")}>
              <Abholung p={p} danach={nachBediener} />
              <Gespann p={p} />
            </Postenteil>
          ) : null}

          {/* ── Der Satz von damals ─────────────────────────────────────
              **Wortgleich mit der alten Schnellerfassung** (`sp-damals`), weil
              es derselbe Befund ist: „beim letzten Mal 475,00 — am 24.08."
              `nurQuelle` heißt, es gibt heute keinen Satz im Stamm und die
              Zahl IST die von damals. Steht heute derselbe Betrag wie damals,
              steht hier gar nichts (`damalsWert`). */}
          {damals && p.vorlage ? (
            <p className="ml-damals">
              {damals.nurQuelle
                ? t("schnell.zeilePreisVonDamals", { nr: p.vorlage.nr, datum: p.vorlage.datum })
                : damals.einheitenDamals !== undefined
                  ? t("schnell.zeileDamalsDauer", {
                    betrag: euro(damals.betrag), zahl: damals.einheitenDamals, jetzt: p.einheiten ?? 0,
                    datum: p.vorlage.datum,
                  })
                  : t("schnell.zeileDamals", { betrag: euro(damals.betrag), datum: p.vorlage.datum })}
            </p>
          ) : null}

          {/* ── „Wieder dasselbe Stück" ─────────────────────────────────
              **Der Typ ist die Zusage, das Stück der Wunsch** (Regel 2). Ein
              Haken, der von sich aus aus ist, und daneben die Auskunft, ob es
              im neuen Fenster überhaupt ginge. **Reserviert ist die dritte
              Antwort** (18.09.2026): Wer den Haken trotzdem setzt, nimmt das
              Gerät und löst die Vormerkung (`reservierungUebergehen`). */}
          {miete && p.vorlage?.maschine ? (
            <>
              <label className="hakenzeile ml-wieder">
                <input
                  type="checkbox"
                  checked={stueckGewaehlt}
                  disabled={stueckLage !== "frei" && stueckLage !== "reserviert"}
                  onChange={(e) => aendern(p.id, e.target.checked
                    ? {
                      maschine: wunschstueck,
                      ...(stueckLage === "reserviert" ? { reservierungUebergehen: true } : {}),
                    }
                    : { maschine: undefined, reservierungUebergehen: undefined })}
                />
                <span>
                  {t("schnell.wiederDasselbe", { maschine: p.vorlage.maschine })}{" "}
                  <span className={stueckLage === "frei" ? "ml-wieder-frei" : "ml-wieder-weg"}>
                    {stueckLage === null
                      ? t("allgemein.laedt")
                      : stueckLage === "frei"
                        ? t("schnell.stueckFrei")
                        : stueckLage === "reserviert"
                          ? t(damalsStueck?.reserviert?.imAbschluss ? "lage.imAbschluss" : "lage.reserviert")
                          : stueckLage === "wirdVermietet"
                            ? t("lage.wirdVermietet")
                            : t("schnell.stueckBelegt")}
                  </span>
                </span>
              </label>
              {(stueckLage === "reserviert" || stueckLage === "wirdVermietet") && damalsStueck?.reserviert ? (
                <p className="ml-wieder-wer">
                  {/* Derselbe Tag in derselben Form wie im Nachschlagen;
                      ohne Frist kein „bis" (N7-05). */}
                  {(() => {
                    const r = damalsStueck.reserviert;
                    const satz = vormerkungWer(r, r.frist ? tagMitWochentag(t, r.frist) : "");
                    return t(satz.schluessel, satz.werte);
                  })()}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Die Karten, ohne alles darum herum.
 *
 * **Sie sind der Teil, den alle drei Fassungen teilen** — die Spalte, die
 * Leiste und das Blatt von unten am Telefon (`MerklisteFab.tsx`). Kopf, Summe
 * und die beiden Wege stehen in jeder Fassung woanders und gehören ihr;
 * die Karten sind dieselben (Luca, 18.09.2026).
 */
export function Sammelliste({ oeffneBlatt }: { oeffneBlatt: (id: string) => void }) {
  const { posten } = useMerkliste();
  const namen = useNamen(posten);
  return (
    <>
      <Vorlagenband />
      <ul className="ml-zeilen">
        {posten.map((p) => (
          <Sammelkarte key={p.id} p={p} name={namen(p)} oeffneBlatt={oeffneBlatt} />
        ))}
      </ul>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Woraus die Sammlung wiederholt ist — und was dabei auffällt.

   Zwei Marken, und beide zählen über die **ganze** Sammlung: *„{n} Preise
   sind heute andere als damals"* und *„{n} Preise stammen aus dem alten
   Vorgang — bitte prüfen"*. Das ist der Satz, den jemand am Telefon
   braucht, bevor er die Liste durchgeht; je Karte stünde er fünfmal und
   sagte jedes Mal weniger.

   **Nur, wenn es etwas zu sagen gibt.** Ein Satz „Preise unverändert"
   stünde bei jeder zweiten Wiederholung und würde nicht gelesen
   (28-designkonzept.md). Ohne Vorlage steht hier gar nichts.

   **Sie sitzt in `Sammelliste` und nicht in `Sammlung`**, und das ist der
   Punkt: Die Karten sind der Teil, den alle drei Fassungen teilen — die
   Spalte in der Vermietfläche, die schwebende Leiste und das Blatt am
   Telefon (`MerklisteFab.tsx`). Eine Marke in `Sammlung` fehlte am
   Telefon, also genau dort, wo der Anruf stattfindet.
   ══════════════════════════════════════════════════════════════════ */

function Vorlagenband() {
  const t = useT();
  const { posten, vorlagen, vorlagenLoesen } = useMerkliste();
  if (!vorlagen.nummern.length) return null;
  return (
    <div className="ml-vorlage">
      <p className="ml-vorlage-her">
        {/* Eine Sammlung darf aus zwei alten Vorgängen entstehen — „den Kran
            von August und die Rüttelplatte von Mai". Dann stehen beide
            Nummern da; das Datum ist das des ersten und sagt, wie weit
            zurück es geht. */}
        {t("schnell.ausVorlage", {
          nr: vorlagen.nummern.join(` ${t("allgemein.trennpunkt")} `),
          datum: vorlagen.datum,
        })}
        <button
          type="button"
          className="ml-vorlage-loesen"
          onClick={vorlagenLoesen}
        >
          {t("merkliste.vorlageLoesen")}
        </button>
      </p>
      {vorlagen.anders ? (
        <Marke art="acht" punkt>
          {t(vorlagen.anders > 1 ? "schnell.preisAndersPlural" : "schnell.preisAnders",
            { zahl: vorlagen.anders })}
        </Marke>
      ) : null}
      {vorlagen.ohneStamm ? (
        <Marke art="acht" punkt>
          {t(vorlagen.ohneStamm > 1 ? "schnell.preisOhneStammPlural" : "schnell.preisOhneStamm",
            { zahl: vorlagen.ohneStamm })}
        </Marke>
      ) : null}
      {/* Die Zahl der wiederholten Zeilen — sie sagt, worauf sich die
          beiden Marken beziehen, wenn die Sammlung auch Gemerktes trägt. */}
      {posten.length > vorlagen.zeilen ? (
        <p className="ml-vorlage-wie">
          {t(vorlagen.zeilen === 1 ? "merkliste.vorlageEine" : "merkliste.vorlageViele",
            { zahl: vorlagen.zeilen })}
        </p>
      ) : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Der Inhalt der Sammlung — in der Leiste und in der Spalte derselbe.

   Kopf, „für wen", die Zeilen, die Summe, der Fuß, die beiden Wege. Was
   sich unterscheidet, steht außen herum und nicht hier drin.
   ══════════════════════════════════════════════════════════════════ */

function Sammlung({ oeffneBlatt, oeffneAbschluss, leeren }: {
  oeffneBlatt: (id: string) => void;
  oeffneAbschluss: (art: Abschlussart) => void;
  /**
   * „Leeren" im Kopf — **nur dort, wo es keinen Reset gibt.**
   *
   * In der Vermietfläche setzt der Knopf oben rechts die Frage *und* die
   * Sammlung zurück (`masken/Vermieten.tsx`). Ein zweiter Knopf daneben,
   * der fast dasselbe tut, wäre einer zu viel.
   */
  leeren?: () => void;
}) {
  const t = useT();
  const { wer, summe, stuecke, offene } = useMerkliste();
  /* Ohne Antwort keine Summe (N12-01) — „0,00" wäre eine Aussage. */
  const unterwegs = usePreiseUnterwegs();
  /* B-16 (03.10.2026): Der Außendienst darf merken, aber nicht vermieten —
     „Jetzt vermieten" stand trotzdem da und tat beim Klick nichts, weil
     `/vermieten` für ihn gesperrt ist. Ohne das Recht bleibt nur der
     Angebotsweg. */
  const darfVermieten = useSitzung().darfAuf("/vermieten");
  /* Wer nur liest (P7-03), merkt und rechnet — aber legt nichts an. */
  const schreibt = useDarfSchreiben();

  return (
    <>
      <header className="ml-kopf">
        <b>{t("merkliste.titel")}</b>
        <span className="ml-zahl">
          {stuecke === 1 ? t("merkliste.einPosten") : t("merkliste.posten", { zahl: stuecke })}
        </span>
        {leeren ? <Knopf klein art="stumm" onClick={leeren}>{t("merkliste.leeren")}</Knopf> : null}
      </header>

      {/* **Für wen** — sie steht in der Sammlung und nicht in einer
          Maske. Wer sie in der Vermietfläche oben gewählt hat, sieht
          sie hier wieder; wer aus dem Nachschlagen kommt, sieht
          nichts und wird im Abschluss gefragt. */}
      {wer.nr ? (
        <p
          className="ml-wer"
          title={`${t("merkliste.fuerWen", { kunde: wer.name || wer.nr })}${wer.ort ? ` ${t("allgemein.trennpunkt")} ${wer.ort}` : ""}`}
        >
          {t("merkliste.fuerWen", { kunde: wer.name || wer.nr })}
          {wer.ort ? ` ${t("allgemein.trennpunkt")} ${wer.ort}` : ""}
        </p>
      ) : null}

      <Sammelliste oeffneBlatt={oeffneBlatt} />

      {/* **Was nicht aus der Trefferliste kommt** (18.09.2026): Leistung,
          Ware, Zuschlag, Freitext. Sie steht unter den Karten und über der
          Summe — dort, wo man sie am Telefon sucht: „und dann noch zwei
          Stunden Mulchen". Vier Knöpfe, die umbrechen dürfen; die Miete
          fehlt mit Absicht, sie kommt aus der Liste oben
          (`bausteine/Merkzeile.tsx`). */}
      <ZeileDazu />

      <div className="ml-summe">
        <span>{t("merkliste.summe")}</span>
        <b>{unterwegs ? t("allgemein.zahlOffen") : euro(summe)}</b>
      </div>
      {/* Netto und noch nicht zugesagt: Eingefroren wird bei der
          Annahme (Regel 4), und bis dahin ist jede Zahl von heute.
          Steht eine Zeile ohne Betrag darin, sagt es die Summe — sonst
          wäre sie kleiner als die Wahrheit. */}
      <p className="ml-fuss">
        {offene && !unterwegs
          ? t(offene === 1 ? "merkliste.fussOffenEiner" : "merkliste.fussOffen", { zahl: offene })
          : t("merkliste.fuss")}
      </p>

      {schreibt ? (
      <div className="ml-taten">
        {darfVermieten ? (
          <Knopf art="haupt" onClick={() => oeffneAbschluss("vermieten")}>
            {t("merkliste.vermieten")}
          </Knopf>
        ) : null}
        <Knopf {...(darfVermieten ? {} : { art: "haupt" as const })} onClick={() => oeffneAbschluss("angebot")}>
          {t("merkliste.angebot")}
        </Knopf>
      </div>
      ) : null}
      {darfVermieten || !schreibt ? null : <p className="ml-fuss">{t("merkliste.nurAngebot")}</p>}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Die Spalte in der Vermietfläche.

   Luca: *„Mach dieses Merkliste und so so, dass das wieder eine
   Seitenleiste in dem Vermieten ist … Ist schicker."*

   **Sie steht immer da, auch leer.** Eine Spalte, die erst mit dem ersten
   Posten erscheint, verschiebt im selben Augenblick die Trefferliste, in
   der man gerade gelesen hat — und leer sagt sie, wie sie sich füllt.
   Deshalb ist sie auch nicht in zwei Teile geschnitten wie die Leiste:
   Hier darf die Abfrage nach den Namen laufen, sie steht ohnehin im Bild.

   Sie rollt für sich (`stile/vermieten.css`, `.vm-sammlung`), damit eine
   lange Sammlung die Trefferliste nicht mitzieht.
   ══════════════════════════════════════════════════════════════════ */

export function Merklistenspalte() {
  const t = useT();
  const { posten } = useMerkliste();
  const { abschluss, setzeAbschluss, blatt, setzeBlatt } = useBlaetter();

  return (
    <aside className="vm-sammlung" aria-label={t("merkliste.titel")}>
      {posten.length ? (
        <Sammlung oeffneBlatt={setzeBlatt} oeffneAbschluss={setzeAbschluss} />
      ) : (
        <>
          <header className="ml-kopf"><b>{t("merkliste.titel")}</b></header>
          {/* Der Satz stand am alten Fuß der Fläche und ist seither
              ungenutzt — er sagt genau das, was die leere Spalte sagen
              muss, und liegt in der Gruppe, der die Spalte gehört. */}
          <p className="vm-sammlung-leer">{t("vermieten.sammlungLeer")}</p>
        </>
      )}
      <Blaetter
        blatt={blatt}
        setzeBlatt={setzeBlatt}
        abschluss={abschluss}
        setzeAbschluss={setzeAbschluss}
        fertig={() => setzeAbschluss(null)}
      />
    </aside>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Die schwebende Leiste — überall außer in der Vermietfläche.
   ══════════════════════════════════════════════════════════════════ */

function Leiste() {
  const t = useT();
  const { posten, summe, stuecke, leeren } = useMerkliste();
  const unterwegs = usePreiseUnterwegs();
  const [offen, setzeOffen] = useState(false);
  const { abschluss, setzeAbschluss, blatt, setzeBlatt } = useBlaetter();

  // Ist die Liste leer, ist auch die aufgeklappte Fassung erledigt — sonst
  // stünde ein leerer Kasten da, den niemand mehr zumachen kann.
  useEffect(() => { if (!posten.length) setzeOffen(false); }, [posten.length]);

  const fertig = () => { setzeAbschluss(null); setzeOffen(false); };

  return (
    <>
      {/* **Während ein Blatt darüber liegt, tritt die Leiste zurück.** Beim
          Abschluss, weil dort dieselbe Liste noch einmal steht; beim Blatt am
          Posten, weil die Leiste sonst darauf liegt — sie steht `fixed` unten
          rechts und über dem Deckblatt (`stile/merkliste.css`), und genau
          dort sitzt der Fuß des Blattes mit „Fertig" darin. Am 17.09.2026
          gemessen: Der Knopf war nicht anklickbar. */}
      <div
        className={`ml-leiste${abschluss || blatt ? " weg" : ""}`}
        aria-hidden={abschluss || blatt ? true : undefined}
      >
        {offen ? (
          <div className="ml-blatt" role="group" aria-label={t("merkliste.titel")}>
            <Sammlung
              oeffneBlatt={setzeBlatt}
              oeffneAbschluss={setzeAbschluss}
              leeren={leeren}
            />
          </div>
        ) : null}

        <button
          type="button"
          className="ml-griff"
          aria-expanded={offen}
          onClick={() => setzeOffen((x) => !x)}
        >
          <span className="ml-griff-zahl">{stuecke}</span>
          <span className="ml-griff-wort">{t("merkliste.titel")}</span>
          <b className="ml-griff-betrag">{unterwegs ? t("allgemein.zahlOffen") : euro(summe)}</b>
          <span className="ml-griff-pfeil" aria-hidden="true">{offen ? "▾" : "▴"}</span>
        </button>
      </div>

      <Blaetter
        blatt={blatt}
        setzeBlatt={setzeBlatt}
        abschluss={abschluss}
        setzeAbschluss={setzeAbschluss}
        fertig={fertig}
      />
    </>
  );
}

/**
 * Der Name einer Zeile — aus dem Posten, sonst aus dem Typenstamm.
 *
 * **Warum als Haken und nicht als Funktion.** Der Stamm wird nur geholt,
 * wenn überhaupt ein Posten ohne Namen darinsteht; gefragt wird trotzdem
 * bedingungslos, weil ein Haken nicht bedingt aufgerufen werden darf. Die
 * Abfrage ist dieselbe, die Preisauskunft und Schnellerfassung ohnehin
 * halten — sie kostet also nichts, was nicht schon da wäre.
 */
export function useNamen(posten: Merkposten[]): (p: Merkposten) => string {
  const fehlt = posten.some((p) => !p.bezeichnung && !p.typ);
  const { data } = useMaschinentypen();
  const stamm = useMemo(
    () => new Map((fehlt ? data?.zeilen ?? [] : []).map((x) => [x.id, x.label])),
    [data, fehlt],
  );
  /* Leer heißt: **noch** keiner — der Stamm ist unterwegs. Wer anzeigt,
     setzt dafür ein Wort ein; wer schreibt, wartet lieber. Ein Platzhalter
     im Speicher stünde sonst später auf dem Beleg. */
  return (p: Merkposten) => p.bezeichnung || p.typ || stamm.get(p.typId) || "";
}
